import { useState, useEffect, forwardRef, useImperativeHandle, useMemo, useCallback } from 'react';
import { Col, FormGroup, Label } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import { setField } from 'Features/Learning/BankQuestions/addQuestionSlice';
import RichEditor from 'Component/Editor/RichEditor/RichEditor';
import FormSelectContain from 'Component/Learning/BankQuestion/QuestionsSelectForm';
import Swal from 'sweetalert2';
import { handleWheel } from 'Utils/handleWheel';
import DragpinSection from './components/DragpinSection';
import ClickableSection from './components/ClickableSection';
import InteractiveTypeSection from './components/InteractiveTypeSection';
import MultipleChoiceRows from './components/answerRows/MultipleChoiceRows';
import SentenceArrangementRows from './components/answerRows/SentenceArrangementRows';
import FillInTheBlanksRows from './components/answerRows/FillInTheBlanksRows';
import IsInteractiveSection from './components/IsInteractiveSection';
import StepKelipatanSection from './components/StepKelipatanSection';
import QuestionMainFieldsSection from './components/QuestionMainFieldsSection';
import {
  CLICKABLE_LENGTH_BY_TYPE,
  makeEmptyStrings,
  getShapeVariant,
  getShapePreset,
} from './questionsForm.helpers';
import AudioMediaSection from './components/media/AudioMediaSection';
import VideoMediaSection from './components/media/VideoMediaSection';
import PieChartHint from './components/hints/PieChartHint';
import AddOptionButton from './components/actions/AddOptionButton';
import { useQuestionInteractiveReset } from './hooks/useQuestionInteractiveReset';
import { useClickableInteractivePreset } from './hooks/useClickableInteractivePreset';
import { useDragpinInteractive } from './hooks/useDragpinInteractive';
import { useSequentialInteractive } from './hooks/useSequentialInteractive';
import { useFillInInteractiveTypeTransition } from './hooks/useFillInInteractiveTypeTransition';

const QuestionForm = forwardRef((props, ref) => {
  const { isContentLocked = false } = props;
  const dispatch = useDispatch()
  const data = useSelector(state => state.addQuestion)
  const [stateChoiceFromRedux, setStateChoiceFromRedux] = useState([]);

  const lockedSectionStyle = isContentLocked
    ? { pointerEvents: 'none', opacity: 0.72 }
    : undefined;

  // Normalize flag: beberapa tempat masih pakai boolean / number (1/0)
  const isInteractiveEnabled = useMemo(() => {
    return data.is_interactive === true || data.is_interactive === 1;
  }, [data.is_interactive]);

  const resetChoiceAndAnswer = useCallback((length) => {
    const empty = makeEmptyStrings(length);
    setStateChoiceFromRedux(empty);
    dispatch(setField({ field: "choice", value: empty }));
    dispatch(setField({ field: "answer", value: empty }));
  }, [dispatch]);

  // Flag khusus: fill_in_the_blanks + interactive + clickable (nilai ditentukan via klik Konva)
  const { isClickableInteractiveFillIn, handleChangeClickablePreset } = useClickableInteractivePreset({
    type: data.type,
    interactiveType: data.interactive_type,
    clickableType: data.clickable_type,
    answer: data.answer,
    isInteractiveEnabled,
    dispatch,
    setField,
    CLICKABLE_LENGTH_BY_TYPE,
  });

  const {
    isDragpinInteractiveSentence,
    dragpinPinsRoom,
    dragPinsRoomOptions,
    handleSelectChangeDragPinsRooms,
    dragPinsPinsOptions,
    handleSelectChangeDragPinsPins,
    dragPinsVisibleNames,
    dragPinsRoomOptionsBySelectedRoomCount,
    handleSelectChangeDragPinPinRoom,
    dragpinPinsMeta,
    handleChangeDragpinPinLabel,
  } = useDragpinInteractive({
    data,
    dispatch,
    setField,
    isInteractiveEnabled,
  });

  const {
    handleChangeSequentialAnswerOnly,
    triggerSetReduxData,
  } = useSequentialInteractive({
    type: data.type,
    interactiveType: data.interactive_type,
    isInteractiveEnabled,
    answer: data.answer,
    choice: data.choice,
    dispatch,
    setField,
    stateChoiceFromRedux,
    setStateChoiceFromRedux,
  });

  useFillInInteractiveTypeTransition({
    type: data.type,
    isInteractiveEnabled,
    interactiveType: data.interactive_type,
    clickableType: data.clickable_type,
    dispatch,
    setField,
    CLICKABLE_LENGTH_BY_TYPE,
    resetChoiceAndAnswer,
  });

  const dataOptions = useMemo(() => {
    // sentence_arrangement: tetap untuk chart/sequential (tanpa clickable & dragpin)
    if (data.type === 'sentence_arrangement') {
      return [
        { value: 'pie_chart', label: 'Pie Chart' },
        { value: 'bar_chart', label: 'Bar Chart' },
        { value: 'line_chart', label: 'Line Chart' },
        { value: 'sequential', label: 'Sambung Titik Berurutan' },
      ];
    }

    // fill_in_the_blanks: pindahan clickable + dragpin
    if (data.type === 'fill_in_the_blanks') {
      return [
        { value: 'clickable', label: 'Pola Bentuk' },
        { value: 'dragpin', label: 'Penempatan (Drag Pin)' },
      ];
    }

    return [];
  }, [data.type]);

  const dataOptionsClickable = [{ value: "triangle", label: "Segitiga" }, { value: "box", label: "Persegi" }, { value: "boxhard", label: "Persegi Kompleks" }];

  const parentTitle = data.interactive_type ? dataOptions.find(item => item.value === data.interactive_type)?.label : null;

  const handleSelectChange = (selectedOption) => {
    if (selectedOption !== null) {
      const nextType = selectedOption.value;

      dispatch(setField({ field: 'interactive_type', value: nextType }));

      // ✅ Step hanya relevan untuk bar_chart / line_chart.
      // Kalau pindah ke tipe lain (termasuk sequential), step harus dibersihkan supaya tidak "nyangkut" dan ikut terkirim.
      const needsStep = nextType === 'bar_chart' || nextType === 'line_chart';
      if (!needsStep) {
        dispatch(setField({ field: 'step', value: '' }));
      }

      return;
    }

    dispatch(setField({ field: 'interactive_type', value: '' }));
    dispatch(setField({ field: 'step', value: '' }));
  };

  const titleClick = data.clickable_type ? dataOptionsClickable.find(item => item.value === data.clickable_type)?.label : null;

  const handleSelectChangeClickable = (selectedOption) => {
    if (selectedOption !== null) {
      dispatch(setField({ field: 'clickable_type', value: selectedOption.value }));
    } else {
      dispatch(setField({ field: 'clickable_type', value: '' }));
    }
  };

  useImperativeHandle(ref, () => ({
    triggerSetReduxData
  }));

  // set useEffect to setStateChoiceFromRedux when data.choice is updated
  useEffect(() => {
    // Guard: biar RichEditor dapat string/array yang valid saat edit
    setStateChoiceFromRedux(Array.isArray(data.choice) ? data.choice : []);
  }, [data.choice]);

  useEffect(() => {
    if (data.answer.length === 0) {
      dispatch(setField({ field: 'answer', value: [''] }));
    }
  }, [data.answer.length, dispatch]);

  useQuestionInteractiveReset({
    type: data.type,
    isInteractive: data.is_interactive,
    interactiveType: data.interactive_type,
    clickableType: data.clickable_type,
    choice: data.choice,
    answer: data.answer,
    dispatch,
    setField,
    resetChoiceAndAnswer,
    CLICKABLE_LENGTH_BY_TYPE,
  });

  const handleAddOption = () => {
    const updatedOptions = [...data.answer];
    updatedOptions.push('');
    const updatedChoice = [...data.choice];
    updatedChoice.push('');
    dispatch(setField({ field: 'answer', value: updatedOptions }));
    dispatch(setField({ field: 'choice', value: updatedChoice }));
  };

  const handleRemoveOption = (index) => {
    const updatedOptions = [...data.choice];
    updatedOptions.splice(index, 1);
    dispatch(setField({ field: 'choice', value: updatedOptions }));
    const updatedAnswer = [...data.answer];
    updatedAnswer.splice(index, 1);
    dispatch(setField({ field: 'answer', value: updatedAnswer }));
  };

  const handleChangeChoice = (index, value) => {
    setStateChoiceFromRedux((prevChoices) => {
      const updatedChoices = [...prevChoices];
      updatedChoices[index] = value;
      dispatch(setField({ field: 'choice', value: updatedChoices }));
      return updatedChoices;
    });
  }

  const handleChangeAnswer = (e, index, field) => {
    const value = e.target.value;
    const updatedOptions = [...data.answer];
    updatedOptions[index] = value;
    dispatch(setField({ field: field, value: updatedOptions }));
  };

  const handleChangeTitle = (e, field) => dispatch(setField({ field: field, value: e.target.value }));

  const handleChangeDescription = (e) => dispatch(setField({ field: 'description', value: e }));

  const handleChangeExplanation = (e) => {
    const cleanedDescription = e.replace(/<\/?[^>]+(>|$)/g, '');

    if (cleanedDescription.trim() === '') {
      dispatch(setField({ field: 'answer_explanation', value: '' }));
    } else {
      dispatch(setField({ field: 'answer_explanation', value: e }));
    }
  }

  const handleChangeAnswer_explanation_video = ({ meta, file }, status) => {
    if (status === 'removed') {
      dispatch(setField({ field: 'answer_explanation_video', value: '' }));
    } else if (status === 'done') {
      dispatch(setField({ field: 'answer_explanation_video', value: file }));
    }
  };

  const handleChangeAudio = ({ meta, file }, status) => {
    if (status === 'removed') {
      dispatch(setField({ field: 'audio_source', value: '' }));
    } else if (status === 'done') {
      dispatch(setField({ field: 'audio_source', value: file }));
    }
  };

  const handleDeleteFile = (e, type) => {
    e.preventDefault();
    Swal.fire({
      title: 'Apakah anda yakin?',
      text: `Anda tidak dapat mengembalikan Video Pembahasan yang telah dihapus!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33'
    }).then(async (result) => {
      if (result.isConfirmed) {
        if (type === "answer_explanation_video") {
          dispatch(setField({ field: 'answer_explanation_video', value: 'delete' }));
          dispatch(setField({ field: 'answer_explanation_videoLink', value: '' }));
        } else if (type === "audio_source") {
          dispatch(setField({ field: 'audio_source', value: 'delete' }));
          dispatch(setField({ field: 'audio_sourceLink', value: '' }));
        }
      }
    })
  }

  const handleChangeStep = (e) => {
    const raw = e.target.value;
    dispatch(setField({ field: 'step', value: raw }));
  };

  // Handler untuk update custom room name (disimpan di interactive_detail)
  const handleChangeRoomCustomName = useCallback((roomIndex, value) => {
    const currentDetail = data.interactive_detail || {};
    const updated = Array.isArray(currentDetail.room_custom_names) ? [...currentDetail.room_custom_names] : [];
    updated[roomIndex] = value;
    dispatch(setField({
      field: 'interactive_detail',
      value: { ...currentDetail, room_custom_names: updated }
    }));
  }, [data.interactive_detail, dispatch]);

  // Handler untuk update custom pin name (disimpan di interactive_detail)
  const handleChangePinCustomName = useCallback((pinIndex, value) => {
    const currentDetail = data.interactive_detail || {};
    const updated = Array.isArray(currentDetail.pin_custom_names) ? [...currentDetail.pin_custom_names] : [];
    updated[pinIndex] = value;
    dispatch(setField({
      field: 'interactive_detail',
      value: { ...currentDetail, pin_custom_names: updated }
    }));
  }, [data.interactive_detail, dispatch]);

  const showPieChartHint = useMemo(() => {
    return isInteractiveEnabled &&
      data.type === "sentence_arrangement" &&
      data.interactive_type === 'pie_chart';
  }, [isInteractiveEnabled, data.type, data.interactive_type]);

  const showAddOptionButton = useMemo(() => {
    return !isDragpinInteractiveSentence &&
      (data.type === 'fill_in_the_blanks' || data.type === 'sentence_arrangement' || data.type === 'multiple_choice') &&
      data.interactive_type !== "clickable";
  }, [isDragpinInteractiveSentence, data.type, data.interactive_type]);

  return (
    <Col sm="12">
      <QuestionMainFieldsSection
        title={data.title}
        onChangeTitle={(e) => handleChangeTitle(e, 'title')}
        description={data.description}
        answerExplanation={data.answer_explanation}
        onChangeDescription={handleChangeDescription}
        choice={data.choice}
        answer={data.answer}
        questionType={data.type}
        interactiveType={data.interactive_type}
        clickableType={data.clickable_type}
        clickableVariant={getShapeVariant(data.clickable_type)}
        clickablePreset={getShapePreset(data.answer, data.clickable_type)}
        dragpinRoomCount={data?.room_count}
        dragpinPinCount={data?.pin_count}
        dragpinPinCustomNames={data?.interactive_detail?.pin_custom_names}
        dragpinRoomCustomNames={data?.interactive_detail?.room_custom_names}
        dragpinPins={data?.interactive_detail?.dragpin_pins}
        dragpinAnswer={data?.answer}
        dragpinKeyAnswer={data?.key_answer}
        barChartStep={data.step}
        barChartMinMax={data.minMax}
        lineChartStep={data.step}
        lineChartMinMax={data.minMax}
        pieChartMinProportion={data.min_proportion}
        isTitleLocked={isContentLocked}
      />

      <div style={lockedSectionStyle} aria-disabled={isContentLocked || undefined}>
        <FormSelectContain />

        {(data.type === 'sentence_arrangement' || data.type === 'fill_in_the_blanks') && (
          <IsInteractiveSection
            value={data.is_interactive}
            onSetFalse={() => dispatch(setField({ field: 'is_interactive', value: false }))}
            onSetTrue={() => dispatch(setField({ field: 'is_interactive', value: true }))}
            isDisabled={data.isUpdate}
          />
        )}

        {isInteractiveEnabled && (data.type === "sentence_arrangement" || data.type === "fill_in_the_blanks") && (
          <InteractiveTypeSection
            options={dataOptions}
            onChange={handleSelectChange}
            value={{ value: data.interactive_type, label: parentTitle }}
            isDisabled={data.isUpdate}
          />
        )}

        {/* clickable sekarang muncul untuk fill_in_the_blanks (bukan sentence_arrangement) */}
        {(data.type === "fill_in_the_blanks" && isInteractiveEnabled && data.interactive_type === "clickable") && (
          <ClickableSection
            clickableType={data.clickable_type}
            titleClick={titleClick}
            options={dataOptionsClickable}
            onChangeClickableType={handleSelectChangeClickable}
            isDisabled={data.isUpdate}
            isPresetDisabled={false}
            variant={getShapeVariant(data.clickable_type)}
            preset={getShapePreset(data.answer, data.clickable_type)}
            onChangePreset={handleChangeClickablePreset}
          />
        )}
      </div>

      {/* dragpin: deskripsi pin editable; room/count/penempatan tetap dikunci saat answered */}
      {(data.type === "fill_in_the_blanks" && isInteractiveEnabled && data.interactive_type === "dragpin") && (
        <DragpinSection
          data={data}
          isUpdate={data.isUpdate}
          isAnswerKeyLocked={isContentLocked}
          dragPinsRoomOptions={dragPinsRoomOptions}
          onChangeRoomCount={handleSelectChangeDragPinsRooms}
          dragPinsPinsOptions={dragPinsPinsOptions}
          onChangePinCount={handleSelectChangeDragPinsPins}
          dragPinsVisibleNames={dragPinsVisibleNames}
          dragpinPinsRoom={dragpinPinsRoom}
          dragPinsRoomOptionsBySelectedRoomCount={dragPinsRoomOptionsBySelectedRoomCount}
          onChangePinRoom={handleSelectChangeDragPinPinRoom}
          dragpinPinsMeta={dragpinPinsMeta}
          onChangePinLabel={handleChangeDragpinPinLabel}
          onChangeRoomCustomName={handleChangeRoomCustomName}
          onChangePinCustomName={handleChangePinCustomName}
        />
      )}

      {/* MC: teks pilihan editable; benar/salah dikunci saat answered */}
      {!isDragpinInteractiveSentence && data.type === 'multiple_choice' && (
        <MultipleChoiceRows
          answers={data.answer}
          stateChoiceFromRedux={stateChoiceFromRedux}
          handleChangeChoice={handleChangeChoice}
          handleChangeAnswer={handleChangeAnswer}
          handleRemoveOption={handleRemoveOption}
          isAnswerLocked={isContentLocked}
        />
      )}

      <div style={lockedSectionStyle} aria-disabled={isContentLocked || undefined}>
        {!isDragpinInteractiveSentence && (data.type === 'sentence_arrangement' || data.type === 'fill_in_the_blanks') && (
          <>
            {data.type === 'sentence_arrangement' && (
              <SentenceArrangementRows
                data={data}
                answers={data.answer}
                stateChoiceFromRedux={stateChoiceFromRedux}
                handleChangeChoice={handleChangeChoice}
                handleChangeAnswer={handleChangeAnswer}
                handleRemoveOption={handleRemoveOption}
                handleChangeSequentialAnswerOnly={handleChangeSequentialAnswerOnly}
              />
            )}

            {data.type === 'fill_in_the_blanks' && (
              !isClickableInteractiveFillIn ? (
                <FillInTheBlanksRows
                  data={data}
                  answers={data.answer}
                  handleChangeAnswer={handleChangeAnswer}
                  handleRemoveOption={handleRemoveOption}
                />
              ) : null
            )}
          </>
        )}
        {(isInteractiveEnabled && data.type === "sentence_arrangement" && data.interactive_type === 'pie_chart') && (
          <PieChartHint isVisible={showPieChartHint} />
        )}

        {!isDragpinInteractiveSentence && ((data.type === 'fill_in_the_blanks' || data.type === 'sentence_arrangement' || data.type === 'multiple_choice') && data.interactive_type !== "clickable") && (
          <AddOptionButton isVisible={showAddOptionButton} onAdd={handleAddOption} />
        )}

        {(isInteractiveEnabled && data.type === "sentence_arrangement" && (data.interactive_type === 'bar_chart' || data.interactive_type === 'line_chart')) && (
          <StepKelipatanSection
            value={data.step || data?.interactive_detail?.[0]?.step || ''}
            onChange={handleChangeStep}
            onWheel={handleWheel}
          />
        )}
      </div>

      <FormGroup className='mb-3'>
        <Label for="validationAnswerExplain" style={{ fontWeight: 'bold' }}>{'Penjelasan Kunci Jawaban'}:</Label>
        <RichEditor id="validationAnswerExplain" handleChangeDescription={handleChangeExplanation} description={data.answer_explanation} />
      </FormGroup>
      <AudioMediaSection
        isUpdate={data.isUpdate}
        audioLink={data.audio_sourceLink}
        onDelete={(e) => handleDeleteFile(e, 'audio_source')}
        onChangeStatus={handleChangeAudio}
        accept=".mp3"
      />
      <VideoMediaSection
        isUpdate={data.isUpdate}
        videoLink={data.answer_explanation_videoLink}
        onDelete={(e) => handleDeleteFile(e, 'answer_explanation_video')}
        onChangeStatus={handleChangeAnswer_explanation_video}
        accept=".mp4"
        width={720}
      />
    </Col>
  );
});

export default QuestionForm;